import { Skeleton } from '@monksflow/monks-ui';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomWorkflowDetail } from '@/shared/hooks';
import { logger } from '@/lib/utils/logger';
import {
  CustomWorkflowInlineInputProps,
  CustomWorkflowInputComponents,
  InputTypes,
  PipelineInput,
  InputField,
  FieldProps,
} from './CustomWorkflowInlineInput.types';

/**
 * Type guard to validate if the value is a valid PipelineInput array
 */
function isPipelineInputArray(value: unknown): value is PipelineInput[] {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every((item): item is PipelineInput => {
    if (typeof item !== 'object' || item === null) {
      return false;
    }
    const record = item as Record<string, unknown>;
    return (
      'name' in record &&
      'title' in record &&
      'dataSchema' in record &&
      'config' in record &&
      typeof record.name === 'string' &&
      typeof record.dataSchema === 'object' &&
      typeof record.config === 'object'
    );
  });
}

type FieldComponentProps = FieldProps & {
  components: CustomWorkflowInputComponents;
};

const TextField = ({ field, value, onChange, components }: FieldComponentProps) => {
  const { TextInput } = components;
  const updateTextField = useCallback(
    (newValue: string) => {
      onChange(field.name, newValue);
    },
    [field.name, onChange],
  );

  return (
    <TextInput
      label={field.label}
      defaultText={value?.toString() || ''}
      onChange={updateTextField}
    />
  );
};

const NumberField = ({
  field,
  value,
  onChange,
  components,
  isInteger = false,
}: FieldComponentProps & { isInteger?: boolean }) => {
  const { NumberInput } = components;
  const updateNumberField = useCallback(
    (newValue: number) => {
      onChange(field.name, newValue);
    },
    [field.name, onChange],
  );

  return (
    <NumberInput
      label={field.label}
      defaultValue={typeof value === 'number' ? value : undefined}
      min={field.minimum}
      max={field.maximum}
      isInteger={isInteger}
      onChange={updateNumberField}
    />
  );
};

const EnumField = ({ field, value, onChange, components }: FieldComponentProps) => {
  const { t } = useTranslation('trackers');

  const { OptionDropdown } = components;
  const updateEnumField = useCallback(
    (newValue: string) => {
      onChange(field.name, newValue);
    },
    [field.name, onChange],
  );

  const options = useMemo(() => {
    if (!field.enumOptions) return [];
    return field.enumOptions.map((option) => ({
      label: option,
      value: option,
    }));
  }, [field.enumOptions]);

  const currentValue = value?.toString() || options[0]?.value || '';

  return (
    <OptionDropdown
      label={field.label}
      description={t('customWorkflow.customWorkflowInlineInput.selectField', {
        field: field.label,
      })}
      initialValue={currentValue}
      value={currentValue}
      onChange={updateEnumField}
      options={options}
    />
  );
};

// Module-scope: used inside `inputFields.map`, closes over only
// `data`, `onChange`, and `components`, all threaded as explicit props.
function CustomWorkflowInputField({
  field,
  data,
  onChange,
  components,
}: {
  field: InputField;
  data: Record<string, string | number>;
  onChange: (name: string, value: string | number) => void;
  components: CustomWorkflowInputComponents;
}) {
  const { t } = useTranslation('trackers');

  const fieldProps = {
    field,
    value: data[field.name],
    onChange,
    components,
  };

  switch (field.type) {
    case InputTypes.TEXT:
      return <TextField key={field.name} {...fieldProps} />;
    case InputTypes.NUMBER:
      return <NumberField key={field.name} {...fieldProps} />;
    case InputTypes.INTEGER:
      return <NumberField key={field.name} {...fieldProps} isInteger />;
    case InputTypes.ENUM:
      return <EnumField key={field.name} {...fieldProps} />;
    default:
      return (
        <div key={field.name}>
          {t('customWorkflow.customWorkflowInlineInput.unknownInputType', {
            inputType: field.type,
          })}
        </div>
      );
  }
}

export const CustomWorkflowInlineInput = ({
  customWorkflowId,
  data = {},
  onChange,
  components,
}: CustomWorkflowInlineInputProps) => {
  const { customWorkflow, isLoading } = useCustomWorkflowDetail(customWorkflowId);

  const inputFields: InputField[] = useMemo(() => {
    if (!customWorkflow || !('inputs' in customWorkflow)) return [];

    // Validate the inputs structure using type guard
    if (!isPipelineInputArray(customWorkflow.inputs)) {
      logger.error('Invalid inputs structure received from customWorkflow');
      return [];
    }

    // Type assertion is safe here because we validated with the type guard
    const validatedInputs = customWorkflow.inputs as PipelineInput[];

    return validatedInputs
      .filter((input) => !['prompt', 'attachments'].includes(input.name))
      .map((input) => ({
        name: input.name,
        label: input.title || input.name,
        type: input.dataSchema.type as InputTypes,
        enumOptions: input.config.enumOptions || input.dataSchema.enum,
        minimum: input.config.minimum,
        maximum: input.config.maximum,
      }));
  }, [customWorkflow]);

  const handleFieldChange = useCallback(
    (name: string, value: string | number) => {
      onChange?.({ ...data, [name]: value });
    },
    [data, onChange],
  );

  if (isLoading) return <Skeleton className="h-9 w-50" data-testid="skeleton-loader" />;

  return (
    <>
      {inputFields.map((field) => (
        <CustomWorkflowInputField
          key={field.name}
          field={field}
          data={data}
          onChange={handleFieldChange}
          components={components}
        />
      ))}
    </>
  );
};
